import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Switch} from '@/components/ui/switch'
import {ValidationState} from '@/components/validation-state'
import {EvidenceListItem, UploadTrigger} from '@/components/upload-trigger'
import {isAiAnalyzable, useEvidenceUploadFlow, type Evidence} from '@/hooks/sgsst/evidencias/use-evidence'

/** `showAiToggle` is the owner's per-upload AI opt-in; for members `ai_validate_aportes` governs. */
export function ActionEvidenceBlock({
  businessId,
  rutaDeDescarga,
  items,
  editable,
  showAiToggle,
  onUpload,
}: {
  businessId: string
  rutaDeDescarga: (evidenceId: string) => string
  items: Evidence[]
  editable: boolean
  showAiToggle: boolean
  onUpload: (file: File, validateAi: boolean, onProgress: (pct: number) => void) => Promise<void>
}) {
  const {t} = useTranslation()
  const [validateAi, setValidateAi] = useState(true)
  const {start, progress, error} = useEvidenceUploadFlow((file, onProgress) =>
    onUpload(file, showAiToggle && isAiAnalyzable(file.type) && validateAi, onProgress)
  )

  return (
    <div className="space-y-2">
      {editable && (
        <div className="flex flex-wrap items-center gap-3">
          <UploadTrigger label={t('nota.attachEvidence')} progress={progress} testId="action-evidence" onPick={start} />
          {showAiToggle && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={validateAi}
                onCheckedChange={setValidateAi}
                data-testid="action-ai-toggle"
                aria-label={t('validation.analyze')}
              />
              {t('validation.analyze')}
            </label>
          )}
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="action-evidence-error">
          {error}
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('nota.evidenceEmpty')}</p>
      ) : (
        <ul className="space-y-1.5" data-testid="action-evidence-list">
          {items.map((item) => (
            <EvidenceListItem
              key={item.id}
              item={item}
              ruta={rutaDeDescarga(item.id)}
              testId="action-evidence-item"
              trailing={<ValidationState item={item} businessId={businessId} editable={editable} />}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
